import React, { useEffect, useMemo, useRef, useState } from "react";
import { Grid, Box, Tooltip, Typography } from "@mui/material";
import { Button } from "abs-ui-components";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { API_ROUTES } from "../../ApiRoutes/ApiRoutes";
import RichTextEditor from "../../RichTextEditor/RichTextEditor";

const EmissionFactors = ({ refetchMonitoringData, vesselDetails, vesselMstId }) => {
	const [emissionFactorData, setEmissionFactorData] = useState([]);
	const editorRef = useRef(null);
	const [editorContent, setEditorContent] = useState("");
	const [error, setError] = useState("");
	const [isSavingDescription, setIsSavingDescription] = useState(false);
	const [lastSavedContent, setLastSavedContent] = useState("");

	useEffect(() => {
		const fetchData = async () => {
			try {
				const registry = vesselDetails?.FuelRegistryDtls ?? [];
				const fuelOilData = registry.map((item) => ({
					fuelOilType:item.FuelType,
					cf: item.Cf ?? 0,
				}));
				const emissionFactorDescription = refetchMonitoringData?.emissionFactor?.emissionFactorDescription ?? "";

				setEmissionFactorData([
					{
						emissionFactorDescription,
						fuelOilData,
					},
				]);

				setEditorContent(emissionFactorDescription);
				setLastSavedContent(emissionFactorDescription);
			} catch (err) {
				emitToasterMsg("error", "Data Fetch", err?.errorMessage || err.message);
			}
		};

		if (vesselDetails?.VesselMstId) fetchData();
	}, [vesselDetails, refetchMonitoringData]);

	
	
		const handleSaveRichText = async () => {
		const editorHTML = editorRef.current.getHTML().trim();
		const cleaned = editorHTML
			.replace(/<p><br><\/p>/g, "")
			.replace(/&nbsp;/g, "")
			.trim();
		const hasContent = cleaned.length > 0 && cleaned !== "<p></p>" && cleaned !== "<br>" && cleaned !== "";

		if (!hasContent) {
			setError("Field is required");
			return;
		}


		setIsSavingDescription(true);
		try {
			const fuelOilDataTypes =
				emissionFactorData[0]?.fuelOilData?.map((item) => ({
					fuelOilType: item.fuelOilType,
					cf: String(item.cf ?? ""),
				})) || [];

			const payload = {
				emissionFactorDescription: editorContent,
				fuelOilDataTypes,
			};

			const result = await callApi({
				method: "POST",
				url: API_ROUTES.EmissionFactor(vesselMstId),
				data: payload,
			});

			if (result?.isSuccess) {
				emitToasterMsg("success", "Emission Factor", "Updated successfully");

				setEmissionFactorData((prev) => [{ ...prev[0], emissionFactorDescription: editorContent }]);

				if (typeof refetchMonitoringData === "function") {
					await refetchMonitoringData();
				}
				setError("");
				setLastSavedContent(editorContent);
			} else {
				emitToasterMsg("error", "Emission Factor", result?.errorMessage);
			}
		} catch (err) {
			emitToasterMsg("error", "Emission Factor", err?.errorMessage || err.message);
		} finally {
			setIsSavingDescription(false);
		}
	};
	const isSaveDisabled = isSavingDescription || !editorContent.trim() || editorContent === lastSavedContent;

	const columns = useMemo(
		() => [
			{
				accessorKey: "fuelOilData",
				header: "Fuel Oil Type",
				enableEditing: false,
				Cell: ({ row }) => {
					const fuelOilData = row.original.fuelOilData || [];
					return (
						<Box>
							{fuelOilData.map((item, idx) => (
								<Tooltip
									key={idx}
									title={
										<span>
											Value auto-filled. To update, please modify the details in{" "}
											<a href="/my-fleet" target="_blank" style={{ color: "blue" ,textDecoration: "underline"}}>
												My Fleet
											</a>
											.
										</span>
									}
									arrow
								>
									<div
										style={{
											padding: "4px 0",
											borderBottom: "1px solid #eee",
											cursor: "help",
										}}
									>
										{item.fuelOilType}
									</div>
								</Tooltip>
							))}
						</Box>
					);
				},
			},
			{
				accessorKey: "cfValues",
				header: "CF (t-CO₂ / t-Fuel)",
				enableEditing: false,
				Cell: ({ row }) => {
					const fuelOilData = row.original.fuelOilData || [];
					return (
						<Box>
							{fuelOilData.map((item, idx) => (
								<Tooltip
									key={idx}
									title={
										<span>
											Value auto-filled. To update, please modify the details in{" "}
											<a href="/my-fleet" target="_blank" style={{ color: "blue",textDecoration: "underline" }}>
												My Fleet
											</a>
										</span>
									}
									arrow
								>
									<div
										style={{
											padding: "4px 0",
											borderBottom: "1px solid #eee",
											cursor: "help",
										}}
									>
										{item.cf}
									</div>
								</Tooltip>
							))}
						</Box>
					);
				},
			},
		],
		[]
	);

	const table = useMaterialReactTable({
		columns,
		data: emissionFactorData || [],
		enablePagination: false,
		enableTopToolbar: false,
		enableBottomToolbar: false,
		enableColumnActions: false,
		enableSorting: false,
		enableRowSelection: false,
		enableStickyHeader: true,
		muiTableHeadCellProps: {
			sx: {
				color: "rgb(255 255 255)",
				backgroundColor: "#042a4c",
				position: "sticky",
				borderRight: "1px solid lightgray",
				"&:last-child": { borderRight: "none" },
			},
		},
		muiTablePaperProps: {
			sx: { boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)" },
		},
		muiTableContainerProps: {
			sx: { maxHeight: "520px" },
		},
		displayColumnDefOptions: {
			"mrt-row-actions": {
				muiTableHeadCellProps: {
					sx: {
						borderBottom: "none",
						borderRight: "1px solid lightgray",
					},
				},
			},
		},
		muiTableBodyCellProps: {
			sx: {
				borderRight: "1px solid lightgray",
				"&:last-child": { borderRight: "none" },
			},
		},
		muiTableBodyRowProps: {
			sx: {
				"&:not(:last-of-type)": { borderBottom: "1px solid lightgray" },
				verticalAlign: "top",
			},
		},
		muiTableHeadRowProps: {
			sx: { borderBottom: "2px solid lightgray" },
		},
	});

	return (
		<Box className="fuel-registry-b3 mt-1">
			<Box mt={3}>
				<Grid container spacing={2} alignItems="flex-start">
					<Grid item xs={12} md={2}>
						<Typography variant="body2" fontWeight={600} mb={1}>
							Description <span style={{ color: "red" }}>*</span>
						</Typography>
					</Grid>
					<Grid item xs={12} md={9}>
						<Box border="1px solid #ccc" borderRadius={1} p={1} sx={{ background: "#fff" }}>
							<RichTextEditor
								ref={editorRef}
								value={editorContent || ""}
								onChange={(content) => {
									setEditorContent(content);
									setError("");
								}}
								placeholder="Enter description for ship engines..."
							/>
						</Box>

						{error && (
							<Typography variant="caption" color="error" mt={1}>
								{error}
							</Typography>
						)}

						<Box mt={2} mb={2} display="flex" justifyContent="flex-start">
							<Button onClick={handleSaveRichText} disabled={isSaveDisabled} size="small">
								{isSavingDescription ? "Save" : "Save"}
							</Button>
						</Box>
					</Grid>
				</Grid>
			</Box>

			<MaterialReactTable table={table} />
		</Box>
	);
};

export default EmissionFactors;
